<template>
  <div class="tnd">
    <div class="tnd__header">
      <h2 class="tnd__titulo">
        Tiempo no disponible
        <span class="tnd__rango" v-if="rango">
          Del {{ fmtFecha(rango.desde) }} al {{ fmtFecha(rango.hasta) }}
        </span>
      </h2>
      <SelectorPeriodo :dias="30" @cambio="alCambiarPeriodo" />
    </div>

    <el-alert
      v-if="noDisponible"
      title="Datos no disponibles en este momento"
      description="La fuente de datos no está respondiendo. Volvé a intentar en unos minutos; si sigue igual, avisá a Operaciones."
      type="info"
      :closable="false"
      show-icon
      class="mb-3"
    ></el-alert>

    <div v-else-if="cargando" class="tnd__cargando">Cargando…</div>

    <template v-else>
      <div class="resumen">
        <div class="resumen__item">
          <span class="resumen__valor">{{ fmtPct(resumen.time_no_disp_pct) }}</span>
          <span class="resumen__label">Tiempo no disponible</span>
          <span class="resumen__pie">promedio del periodo</span>
        </div>
        <div class="resumen__item">
          <span class="resumen__valor">{{ resumen.horas_cerradas }} h</span>
          <span class="resumen__label">Horas cerrada</span>
          <span class="resumen__pie">de {{ resumen.horas_programadas }} h programadas</span>
        </div>
        <div class="resumen__item">
          <span class="resumen__valor">{{ diasMedidos }}</span>
          <span class="resumen__label">Días medidos</span>
          <span class="resumen__pie">{{ dias.length - diasMedidos }} sin medición</span>
        </div>
        <div class="resumen__item">
          <span class="resumen__valor" :class="{ 'resumen__valor--alerta': resumen.dias_cerrada_completa > 0 }">
            {{ resumen.dias_cerrada_completa }}
          </span>
          <span class="resumen__label">Días cerrada completa</span>
          <span class="resumen__pie">programada para abrir, sin operar</span>
        </div>
      </div>

      <div class="tnd__toolbar">
        <base-button type="success" size="sm" @click="descargar" :disabled="!dias.length">
          <i class="fas fa-download"></i> Descargar Excel
        </base-button>
      </div>

      <el-table :data="dias" size="small" stripe empty-text="Sin datos de horario en el periodo">
        <el-table-column label="Fecha" width="120" align="center" header-align="center">
          <template slot-scope="s">{{ fmtFecha(s.row.fecha) }}</template>
        </el-table-column>
        <el-table-column label="Horas programadas" width="165" align="center" header-align="center">
          <template slot-scope="s">
            <span v-if="s.row.horas_programadas !== null">{{ s.row.horas_programadas }} h</span>
            <span v-else class="celda-vacia">—</span>
          </template>
        </el-table-column>
        <el-table-column label="Horas abierta" width="140" align="center" header-align="center">
          <template slot-scope="s">
            <span v-if="s.row.horas_operadas !== null">{{ s.row.horas_operadas }} h</span>
            <span v-else class="celda-vacia">—</span>
          </template>
        </el-table-column>
        <el-table-column label="Horas cerrada" width="140" align="center" header-align="center">
          <template slot-scope="s">
            <span v-if="s.row.horas_cerradas === null" class="celda-vacia">—</span>
            <span v-else class="pill" :class="s.row.horas_cerradas > 0 ? 'pill--roja' : 'pill--verde'">
              {{ s.row.horas_cerradas }} h
            </span>
          </template>
        </el-table-column>
        <el-table-column label="% no disponible" width="150" align="center" header-align="center">
          <template slot-scope="s">
            <span v-if="s.row.time_no_disp_pct !== null" class="tnd__pct">
              {{ fmtPct(s.row.time_no_disp_pct) }}
            </span>
            <span v-else class="celda-vacia">N/A</span>
          </template>
        </el-table-column>
        <el-table-column label="Estado" min-width="220">
          <template slot-scope="s">
            <span v-if="s.row.cerrada_todo_el_dia" class="pill pill--roja">Cerrada todo el día</span>
            <span v-else-if="s.row.time_no_disp_estado !== 'ok'" class="tnd__motivo">
              La fuente no reporta horario ese día
            </span>
            <span v-else-if="s.row.horas_cerradas > 0" class="celda-vacia">
              Abrió {{ s.row.horas_cerradas }} h más tarde o cerró antes
            </span>
            <span v-else class="celda-vacia">Abrió todo su horario</span>
          </template>
        </el-table-column>
      </el-table>
    </template>
  </div>
</template>

<script>
import { Alert, Table, TableColumn } from 'element-ui'
import SelectorPeriodo from '@/components/Metrics/SelectorPeriodo.vue'
import MetricasCocinaGateway from '@/gateways/MetricasCocinaGateway'
import { descargarExcel } from '@/utils/excel'

/**
 * Detalle de la card "% Tiempo no disponible": que dias la cocina estuvo
 * cerrada cuando tenia que estar abierta, y cuantas horas.
 *
 * MIDE OTRA COSA QUE "% CARTA ACTIVA", y conviene no confundirlas:
 *
 *   Tiempo no disponible  horas cerradas / horas programadas   -> la COCINA
 *   Carta activa          horas de platos prendidos / horario  -> los PLATOS
 *
 * No son complementarias: no suman 100 (se verifico — 95.06 + 5.88 = 100.94 el
 * 05/08/2026 en la cocina 3).
 *
 * Usa el MISMO endpoint que la pantalla de metricas (/foodcourt/metrics) en
 * vez de uno propio. No es por ahorrar: es para que el porcentaje de esta
 * pantalla y el de la card salgan literalmente del mismo calculo y no puedan
 * divergir. La cocina la resuelve el backend desde el token.
 */
export default {
  name: 'TiempoNoDisponible',
  components: {
    SelectorPeriodo,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    [Alert.name]: Alert
  },
  data () {
    return {
      // Los pone SelectorPeriodo al montarse, antes de la primera carga.
      desde: null,
      hasta: null,
      cargando: true,
      noDisponible: false,
      dias: [],
      resumen: {},
      rango: null
    }
  },
  computed: {
    diasMedidos () {
      return this.dias.filter(d => d.time_no_disp_estado === 'ok').length
    }
  },
  methods: {
    /* SelectorPeriodo emite tambien al montarse: esa es la primera carga. */
    alCambiarPeriodo (rango) {
      this.desde = rango.desde
      this.hasta = rango.hasta
      this.cargar()
    },
    fmtFecha (iso) {
      if (!iso) return ''
      const p = iso.split('-')
      return p[2] + '/' + p[1] + '/' + p[0]
    },
    /* null es "sin medición", nunca 0. */
    fmtPct (v) {
      return v === null || v === undefined ? 'N/A' : v + '%'
    },
    descargar () {
      descargarExcel('tiempo-no-disponible', [{
        nombre: 'Tiempo no disponible',
        columnas: [
          { clave: 'fecha', titulo: 'Fecha', ancho: 12, tipo: 'texto' },
          { clave: 'programadas', titulo: 'Horas programadas', ancho: 18, tipo: 'horas' },
          { clave: 'abierta', titulo: 'Horas abierta', ancho: 15, tipo: 'horas' },
          { clave: 'cerrada', titulo: 'Horas cerrada', ancho: 15, tipo: 'horas' },
          { clave: 'pct', titulo: '% no disponible', ancho: 16, tipo: 'porcentaje' },
          { clave: 'estado', titulo: 'Estado', ancho: 40, tipo: 'texto' }
        ],
        filas: this.dias.map(d => ({
          fecha: this.fmtFecha(d.fecha),
          programadas: d.horas_programadas,
          abierta: d.horas_operadas,
          cerrada: d.horas_cerradas,
          pct: d.time_no_disp_pct,
          estado: this.textoEstado(d)
        }))
      }])
    },
    textoEstado (d) {
      if (d.cerrada_todo_el_dia) return 'Cerrada todo el día'
      if (d.time_no_disp_estado !== 'ok') return 'Sin dato de horario'
      if (d.horas_cerradas > 0) return 'Abrió tarde o cerró antes'
      return 'Abrió todo su horario'
    },
    cargar () {
      this.cargando = true
      this.noDisponible = false


      MetricasCocinaGateway.obtener(this.desde, this.hasta)
        .then(payload => {
          if (payload.status === 'UNAVAILABLE') {
            this.noDisponible = true
            return
          }

          const r = payload.result || {}
          // Mas reciente arriba, igual que el resto de las pantallas.
          this.dias = (r.dias || []).slice().reverse()
          this.resumen = r.resumen || {}
          this.rango = r.rango || null
        })
        .catch(() => { this.noDisponible = true })
        .finally(() => { this.cargando = false })
    }
  }
}
</script>

<style scoped>
.tnd { padding: 28px 34px; }
.tnd__header {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 22px;
}
.tnd__titulo { font-size: 28px; font-weight: 700; color: #1a202c; margin: 0; }
.tnd__rango { font-size: 15px; font-weight: 400; color: #6b7684; margin-left: 14px; }
.tnd__cargando { padding: 60px 0; text-align: center; color: #8a94a0; font-size: 14px; }
.tnd__toolbar { display: flex; justify-content: flex-end; margin-bottom: 16px; }
.tnd__pct { font-weight: 600; }
.tnd__motivo { font-size: 12.5px; color: #a16207; }
.celda-vacia { color: #c2c9d1; }

.resumen { display: flex; flex-wrap: wrap; gap: 12px; margin-bottom: 22px; }
.resumen__item {
  flex: 1;
  min-width: 170px;
  display: flex;
  flex-direction: column;
  gap: 3px;
  padding: 16px 18px;
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 10px;
}
.resumen__valor { font-size: 26px; font-weight: 700; color: #1a202c; line-height: 1.1; }
.resumen__valor--alerta { color: #dc2626; }
.resumen__label { font-size: 13px; font-weight: 600; color: #404040; }
.resumen__pie { font-size: 12px; color: #a3a3a3; }

.pill {
  display: inline-block;
  border-radius: 6px;
  padding: 3px 9px;
  font-size: 11px;
  font-weight: 700;
}
.pill--verde { background: #e8f5ec; color: #01662e; }
.pill--roja { background: #fef2f2; color: #dc2626; }

::v-deep .el-table__header th.is-center .cell { justify-content: center; }
</style>
